import { Injectable, OnDestroy } from "@angular/core";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ItemGroupDataModel, ItemGroupViewModel, ItemGroupMenuViewModel, ProductDataModel4, ScheduledForRequest, ItemTagViewModel, WebshopSettingsDataModel2, WebshopSettingsDMKey, UsingType } from "../../shared/models";
import { UserHelperService } from "../../services/userHelper.service";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ContentType, ImageOrientation, ItemTagStatusType } from "../../shared/contentTypes";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { CoreLogService } from "../../services/log.service";
import { Observable, Subscription } from "rxjs";
import { ProductService } from "../../services/product.service";
import { WebShopService } from "../../services/webShop.service";
import Utils from "../../shared/utils";
import { MenuService } from "../menu/menu.service";
import { ItemTagHelperService } from "../../services/itemTagHelper.service";
import { WebShopMode } from "../../services/IWebShopService";

@Injectable({
  providedIn: "root"
})
export class MenugroupService implements OnDestroy {
  private _itemGroupObsServ: IObjectObserver<ItemGroupDataModel>;
  private _itemGroupsChanged: Subscription;
  private _webshopSettingsObsServ: IObjectObserver<WebshopSettingsDataModel2>;
  private _webshopSettings: WebshopSettingsDataModel2 = null;
  private _webshopSettingsChanged: Subscription;

  private _cached_valide_itemGroups_location: number = null;
  private _cached_valide_itemGroups_serviceMode: WebShopMode = null;
  private _cached_valide_itemGroups: ItemGroupDataModel[] = null;

  constructor(private _coreLogService: CoreLogService,
    private _userHelperService: UserHelperService,
    objObsInjectHelper: CircularInjectionHelper,
    private _webshopService: WebShopService,
    private _itemTagHelperService: ItemTagHelperService,
    private _menuService: MenuService) {
    const me = this;
    me._itemGroupObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ItemGroupDataModel>(ContentType.Item_Group, objObsInjectHelper);
    me._itemGroupsChanged = me._itemGroupObsServ.ObserveChanges.subscribe(() => me._cached_valide_itemGroups = null);
    me._webshopSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, objObsInjectHelper);
    me._webshopSettingsChanged = me._webshopSettingsObsServ.ObserveChanges.subscribe(() => me._webshopSettings = null);
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._itemGroupsChanged) {
      me._itemGroupsChanged.unsubscribe();
    }
    if (me._webshopSettingsChanged) {
      me._webshopSettingsChanged.unsubscribe();
    }
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._itemGroupObsServ.ObserveChanges;
  }

  // mit und ohne location laden, wie für home

  /** ermittllung der anzuzeigenden Warengruppen, welche artikel enthalten, webshop location und service müssen gesetzt sein */
  private async _GetValideItemGroups(): Promise<ItemGroupDataModel[]> {
    const me = this;

    const location = me._webshopService.locationID;
    const serviceMode = me._webshopService.getServiceMode();
    // load ich cache changed
    if (me._cached_valide_itemGroups_location != location || me._cached_valide_itemGroups_serviceMode != serviceMode || Utils.IsNull(me._cached_valide_itemGroups)) {
      if (Utils.IsNull(me._webshopSettings)) {
        me._webshopSettings = await me._webshopSettingsObsServ.GetObject(WebshopSettingsDMKey);
      }

      let isTestUser: boolean = false;
      isTestUser = me._userHelperService.IsTestUser;
      const item_groups = await me._itemGroupObsServ.GetObjectsByFilter(model => !model.deleted && model.release && (model.public || isTestUser));

      // Prüfung ob die Warengruppen auch Artikel enthalten
      let itemTags: ItemTagViewModel[] = [];
      let likeIds: number[] = [];
      let dislikeIds: number[] = [];
      if (me._webshopSettings && me._webshopSettings.is_itemtag_enabled) {
        itemTags = await me._itemTagHelperService.GetProductTags();
        if (itemTags && itemTags.length > 0) {
          likeIds = itemTags.filter(t => t.itemTagStatus === ItemTagStatusType.like).map(m => m.id);
          dislikeIds = itemTags.filter(t => t.itemTagStatus === ItemTagStatusType.dislike).map(m => m.id);
        }
      }
      let products = await me._menuService.GetProducts(location, itemTags, likeIds, dislikeIds);
      if (serviceMode === WebShopMode.Webvoucher) {
        products = products.filter(model => model.usingtype === UsingType.Webvoucher);
      } else {
        products = products.filter(model => model.usingtype !== UsingType.Webvoucher);
      }

      me._cached_valide_itemGroups = item_groups.filter(item_group => products.findIndex(product => product.isInGroup(item_group)) > -1);
      me._cached_valide_itemGroups_location == location;
      me._cached_valide_itemGroups_serviceMode == serviceMode;
    };
    return me._cached_valide_itemGroups
  }

  /**
   * GetAllItemGroups liefert alle angelegten Warengruppen ohne weitere prüfungen
   */
  public GetAllItemGroups(): Promise<ItemGroupViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<ItemGroupViewModel[]>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      me._itemGroupObsServ.GetObjectsByFilter(model => !model.deleted && model.release && (model.public || isTestUser))
        .then(data => {
          resolve(me.mapItemGroupModelListToViewModelList(data));
        })
        .catch((e) => {
          me._coreLogService.error("MenugroupService.GetItemGroup", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * ermittllung der anzuzeigenden Warengruppen, welche artikel enthalten, webshop location und service müssen gesetzt sein
   */
  public GetValideItemGroup(): Promise<ItemGroupViewModel[]> {
    const me = this;
    return new Promise<ItemGroupViewModel[]>((resolve, reject) => {
      me._GetValideItemGroups().then(data => {
        resolve(me.mapItemGroupModelListToViewModelList(data));
      }).catch((e) => {
        me._coreLogService.error("MenugroupService.GetItemGroup", "GetItemGroups", e);
        reject();
      });
    });
  }

  /**
 * GetItemMenuGroup wird genutzt um die Liste der Warenbruppen zu füllen
 * ermittllung der anzuzeigenden Warengruppen, welche artikel enthalten, webshop location und service müssen gesetzt sein
 */
  public GetValideItemMenuGroup(): Promise<ItemGroupMenuViewModel[]> {
    const me = this;
    return new Promise<ItemGroupMenuViewModel[]>((resolve, reject) => {
      me._GetValideItemGroups().then(data => {
        resolve(me.mapItemGroupModelListToItemGroupMenuViewModelList(data));
      })
        .catch((e) => {
          me._coreLogService.error("MenugroupService.GetItemGroup", "GetItemGroups", e);
          reject();
        });
    });
  }

  private mapItemGroupModelListToViewModelList(modelList: ItemGroupDataModel[]): ItemGroupViewModel[] {
    const me = this;
    if (modelList && modelList.length > 0) {
      const result: ItemGroupViewModel[] = [];
      modelList.forEach(model => {
        result.push(me.mapItemGroupModelToViewModel(model));
      });
      return result;
    } else {
      return null;
    }
  }
  private mapItemGroupModelToViewModel(model: ItemGroupDataModel): ItemGroupViewModel {
    const me = this;
    const viewModel = new ItemGroupViewModel();
    viewModel.id = model.id;
    viewModel.sort_nr = model.sort_nr;
    viewModel.use_label = model.use_label;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.item_mappings = model.item_mappings;
    viewModel.imagePhoto = ContentType.Item_Group + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    return viewModel;
  }

  private mapItemGroupModelListToItemGroupMenuViewModelList(modelList: ItemGroupDataModel[]): ItemGroupMenuViewModel[] {
    const me = this;
    if (modelList && modelList.length > 0) {
      const result: ItemGroupMenuViewModel[] = [];
      modelList.forEach(model => {
        result.push(me.mapItemGroupModelToItemGroupMenuViewModel(model));
      });
      return result;
    } else {
      return null;
    }
  }

  private mapItemGroupModelToItemGroupMenuViewModel(model: ItemGroupDataModel): ItemGroupMenuViewModel {
    const me = this;
    const viewModel = new ItemGroupMenuViewModel();
    viewModel.id = model.id;
    viewModel.sort_nr = model.sort_nr;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.item_mappings = model.item_mappings;
    return viewModel;
  }
}
